import React, { useState, useRef } from 'react';
import {
	Upload,
	X,
	Star,
	MoreVertical,
	Trash2,
	Download,
} from 'lucide-react';
import { useClientImages } from '../hooks/useClientImages';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useToast } from '../../../../components/ui/toast';

const ImageSlotManager = ({
	imageType,
	maxSlots,
	title,
	description,
	client,
}) => {
	const {
		images,
		loading,
		error,
		uploadImage,
		deleteImage,
		setPrimary,
		getImagesByType,
	} = useClientImages(client);

	const [showActions, setShowActions] = useState(null);
	const [downloadingImage, setDownloadingImage] = useState(null);
	const fileInputRefs = useRef({});
	const { addToast } = useToast();

	const typeImages = getImagesByType(imageType);

	const handleFileSelect = async (file, slot) => {
		if (!file) return;

		try {
			await uploadImage(imageType, slot, file);
		} catch (err) {
			console.error('Upload failed:', err);
		}
	};

	const handleDelete = async (imageId) => {
		try {
			await deleteImage(imageId);
			setShowActions(null);
		} catch (err) {
			console.error('Delete failed:', err);
		}
	};

	const handleSetPrimary = async (imageId) => {
		try {
			await setPrimary(imageId);
			setShowActions(null);
		} catch (err) {
			console.error('Set primary failed:', err);
		}
	};

	const handleDownload = async (image) => {
		if (!image || !image.imageUrl) return;

		setDownloadingImage(image.id);
		try {
			const response = await axios.get(image.imageUrl, {
				responseType: 'blob',
			});

			// Generate structured filename: username_imagetype_slot_datetime.ext
			const urlParts = image.imageUrl.split('/');
			const originalFilename = urlParts[urlParts.length - 1];
			const extension = originalFilename.split('.').pop() || 'jpg';

			// Use existing filename if it already follows structured naming convention
			// Otherwise generate one
			let filename;
			if (
				originalFilename.includes(client.username) &&
				originalFilename.includes(imageType)
			) {
				filename = originalFilename;
			} else {
				const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
				filename = `${client.username}_${imageType}_${image.imageSlot}_${timestamp}.${extension}`;
			}

			saveAs(response.data, filename);

			addToast({
				title: 'Download Complete',
				description: `Image downloaded successfully as ${filename}`,
				type: 'success',
			});
		} catch (error) {
			console.error('Download error:', error);
			addToast({
				title: 'Download Failed',
				description: `Could not download image. Please try again.`,
				type: 'error',
			});
		} finally {
			setDownloadingImage(null);
			setShowActions(null);
		}
	};

	const getSlotName = (slot) => {
		switch (imageType) {
			case 'logo':
				return `Logo ${slot}`;
			case 'broker_logo':
				return `Logo ${slot}`;
			case 'headshot':
				return `Image ${slot}`;
			case 'lifestyle':
				return `Image ${slot}`;
			default:
				return `Image ${slot}`;
		}
	};

	// Render a single slot
	const renderSlot = (slot) => {
		const image = typeImages.find((img) => img.imageSlot === slot);
		const isEmpty = !image;
		const isPrimary = image?.isPrimary;

		return (
			<div
				key={slot}
				className={`rounded-lg border transition-all duration-200 ${isEmpty ? 'bg-stone-50/50 border-gray-100' : 'bg-white border-gray-100 shadow-sm hover:border-primary/30'}`}
				onMouseLeave={() => setShowActions(null)}>
				{/* Header */}
				<div className='px-3 py-2 border-b border-gray-100 flex items-center justify-between'>
					<div className='flex items-center gap-2'>
						<span className='text-xs font-montserrat-medium text-gray-500'>
							{getSlotName(slot)}
						</span>
						{isPrimary && (
							<Star className='w-4 h-4 text-yellow-500 fill-current' />
						)}
					</div>

					{!isEmpty && (
						<div className='relative'>
							<button
								onClick={() =>
									setShowActions(showActions === slot ? null : slot)
								}
								className='p-1 hover:bg-gray-100 rounded'>
								<MoreVertical className='w-4 h-4 text-gray-500' />
							</button>

							{showActions === slot && (
								<div className='absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]'>
									<button
										onClick={() => handleDownload(image)}
										disabled={downloadingImage === image.id}
										className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'>
										<Download className='w-3 h-3' />
										{downloadingImage === image.id
											? 'Downloading...'
											: 'Download'}
									</button>
									<button
										onClick={() =>
											fileInputRefs.current[`replace-${slot}`]?.click()
										}
										className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50'>
										Replace
									</button>
									{!isPrimary && (
										<button
											onClick={() => handleSetPrimary(image.id)}
											className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50'>
											Set Primary
										</button>
									)}
									<button
										onClick={() => handleDelete(image.id)}
										className='w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50'>
										Delete
									</button>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Content */}
				<div className='p-2'>
					{isEmpty ? (
						<div className='aspect-square'>
							<div
								className='w-full h-full border border-dashed border-primary/20 rounded-lg text-center hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 cursor-pointer flex flex-col justify-center items-center group'
								onClick={() => fileInputRefs.current[slot]?.click()}>
								<Upload className='w-5 h-5 text-primary/30 group-hover:text-primary/50 mb-1.5 transition-colors duration-200' />
								<p className='text-xs font-montserrat-medium text-gray-400 group-hover:text-gray-500 transition-colors'>Upload</p>
							</div>
						</div>
					) : (
						<div className='space-y-2'>
							{/* Outer square acts as the aspect-ratio box */}
							<div className='aspect-square w-full rounded-lg relative overflow-hidden group flex items-center justify-center'>
								<img
									src={image.imageUrl}
									alt={image.caption || `${imageType} ${slot}`}
									className='w-full h-full object-cover object-center'
								/>
								<div className='absolute inset-0 w-full h-full pointer-events-none bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all' />
							</div>

							{image.caption && (
								<div className='bg-gray-50 rounded p-2'>
									<p className='text-xs text-gray-500'>Caption:</p>
									<p className='text-sm text-gray-800'>{image.caption}</p>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Hidden file inputs */}
				<input
					ref={(el) => (fileInputRefs.current[slot] = el)}
					type='file'
					accept='image/*'
					className='hidden'
					onChange={(e) => handleFileSelect(e.target.files[0], slot)}
				/>
				<input
					ref={(el) => (fileInputRefs.current[`replace-${slot}`] = el)}
					type='file'
					accept='image/*'
					className='hidden'
					onChange={(e) => handleFileSelect(e.target.files[0], slot)}
				/>
			</div>
		);
	};

	if (loading) {
		return (
			<div className='text-center py-6'>
				<div className='inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-primary'></div>
				<p className='mt-2 text-xs font-montserrat-regular text-gray-400'>Loading images...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className='text-center py-8'>
				<p className='text-red-600'>Error loading images: {error}</p>
			</div>
		);
	}

	// Generate slots array based on maxSlots
	const slots = Array.from({ length: maxSlots }, (_, i) => i + 1);

	return (
		<div className='space-y-4'>
			<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
				{slots.map((slot) => renderSlot(slot))}
			</div>
		</div>
	);
};

export default ImageSlotManager;
